import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from '../../context/LanguageContext';
import iconAccident from '../../assets/icons/accident.png';
import iconInsurance from '../../assets/icons/insurance.png';
import iconTerm from '../../assets/icons/term.png';
import { consultancyHeroImage } from '../../assets/siteImages';
import { cardStagger, fadeUp, scrollViewport } from '../../motion/presets';
import '../../components/home-focus/home-focus.scss';
import '../../styles/page-hero-media.scss';
import './consultancy.scss';

const PLAN_META = [
  { id: 'term', theme: 'work', icon: iconTerm },
  { id: 'health', theme: 'education', icon: iconInsurance },
  { id: 'accident', theme: 'insurance', icon: iconAccident },
];

const Consultancy = () => {
  const { content } = useTranslation();
  const p = content.consultancyPage;

  return (
    <main className="consultancy-page">
      <section className="consultancy-hero section-full" aria-labelledby="consultancy-hero-title">
        <div className="container page-hero-split">
          <div className="page-hero-split__copy">
            <motion.p
              className="consultancy-hero__eyebrow"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              {p.heroEyebrow}
            </motion.p>
            <motion.h1
              id="consultancy-hero-title"
              className="consultancy-hero__title"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              {p.heroTitle}
            </motion.h1>
            <motion.p
              className="consultancy-hero__lead"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              {p.heroLead}
            </motion.p>
            <motion.div
              className="consultancy-hero__cta"
              initial="hidden"
              whileInView="visible"
              viewport={scrollViewport}
              variants={fadeUp}
            >
              <Link className="btn btn-primary consultancy-hero__btn" to="/contact?purpose=consultancy">
                {p.contactNow}
              </Link>
            </motion.div>
          </div>
          <motion.figure
            className="page-hero-split__media"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <img
              className="page-hero-split__photo"
              src={consultancyHeroImage}
              alt={p.heroPhotoAlt}
              loading="eager"
              decoding="async"
            />
          </motion.figure>
        </div>
      </section>

      <section className="consultancy-plans section-full" aria-labelledby="consultancy-plans-title">
        <div className="container">
          <motion.h2
            id="consultancy-plans-title"
            className="consultancy-plans__title"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {p.plansTitle}
          </motion.h2>
          <motion.p
            className="consultancy-plans__lead"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {p.plansLead}
          </motion.p>
          <motion.div
            className="home-focus__grid consultancy-plans__grid"
            variants={cardStagger}
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
          >
            {PLAN_META.map((meta, i) => {
              const copy = p.plans[i];
              return (
                <motion.article
                  key={meta.id}
                  className={`home-focus__card home-focus__card--${meta.theme}`}
                  variants={fadeUp}
                >
                  <div className="home-focus__card-top">
                    <div className="home-focus__icon-wrap" aria-hidden="true">
                      <img
                        src={meta.icon}
                        alt=""
                        className="home-focus__icon"
                        width={56}
                        height={56}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>
                  </div>
                  <h3 className="home-focus__card-title">{copy.title}</h3>
                  <p className="home-focus__card-text">{copy.body}</p>
                  <Link className="home-focus__card-link" to="/contact?purpose=consultancy">
                    {p.contactNow} <span aria-hidden="true">→</span>
                  </Link>
                </motion.article>
              );
            })}
          </motion.div>
        </div>
      </section>

      <section className="consultancy-steps section-full" aria-labelledby="consultancy-steps-title">
        <div className="container consultancy-steps__inner">
          <motion.h2
            id="consultancy-steps-title"
            className="consultancy-steps__title"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {p.stepsTitle}
          </motion.h2>
          <ol className="consultancy-steps__list">
            {p.steps.map((step) => (
              <motion.li
                key={step.n}
                className="consultancy-steps__item"
                initial="hidden"
                whileInView="visible"
                viewport={scrollViewport}
                variants={fadeUp}
              >
                <span className="consultancy-steps__badge" aria-hidden="true">
                  {step.n}
                </span>
                <div>
                  <h3 className="consultancy-steps__item-title">{step.title}</h3>
                  <p className="consultancy-steps__item-text">{step.body}</p>
                </div>
              </motion.li>
            ))}
          </ol>
          <motion.p
            className="consultancy-steps__master"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            {p.masterLineBefore} <strong>{p.masterLineStrong}</strong> {p.masterLineAfter}
          </motion.p>
          <motion.blockquote
            className="consultancy-steps__quote"
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <p>{p.quoteBody}</p>
            <footer>{p.quoteFooter}</footer>
          </motion.blockquote>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={scrollViewport}
            variants={fadeUp}
          >
            <Link className="btn btn-primary consultancy-steps__btn" to="/contact?purpose=consultancy">
              {p.contactNow}
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default Consultancy;
